import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ChevronRight, CheckCircle, Award, BarChart3, Clock, Bookmark, Download } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useCourse } from '../context/CourseContext';
import Header from '../components/Header';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0217f32c-9734-4d94-b36b-1239c75b1220/images/4d878b6891ae5d911e7101ff97d8cfe21409bb8fbb770553f703663102722b60.png";

export default function CourseDashboard() {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setTotalSubtopics, getOverallProgress, completedSubtopics, completedQuizzes, getSectionProgress, isQuizComplete, bookmarks } = useCourse();

  useEffect(() => {
    axios.get(`${API}/course`).then(res => {
      setCourse(res.data);
      setTotalSubtopics(res.data.total_subtopics);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load course:', err);
      setLoading(false);
    });
  }, [setTotalSubtopics]);

  const progress = getOverallProgress();

  useEffect(() => {
    if (progress === 100 && Object.keys(completedQuizzes).length >= 21) {
      window.parent.postMessage({ type: "COURSE_COMPLETED" }, "*");
    }
  }, [progress, completedQuizzes]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 rounded-lg bg-sf-blue flex items-center justify-center mx-auto mb-3 animate-pulse">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <p className="text-sm text-muted-foreground">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Failed to load course data.</p>
      </div>
    );
  }

  const completedSections = course.sections.filter(s => {
    const stIds = s.subtopics.map(st => st.id);
    return getSectionProgress(stIds) === 100 && isQuizComplete(s.id);
  }).length;

  return (
    <div className="min-h-screen bg-background" data-testid="course-dashboard">
      <Header courseSections={course?.sections} />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="opacity-0 animate-fade-up">
              <span className="text-xs font-medium text-sf-blue bg-sf-blue-light px-3 py-1.5 rounded-full uppercase tracking-widest">
                Complete Training Guide
              </span>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-foreground mt-5 mb-4">
                {course.title}
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                {course.description}. Master {course.total_sections} modules with {course.total_subtopics} topics and interactive quizzes.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <Button
                  onClick={() => navigate('/section/1')}
                  className="bg-sf-blue hover:bg-sf-blue-dark text-white px-6 h-11"
                  data-testid="start-course-btn"
                >
                  {progress > 0 ? 'Continue Learning' : 'Start Course'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              <div className="text-sm text-muted-foreground mt-3">
                <span className="font-semibold text-foreground">{course.total_sections}</span> Sections
                <span className="mx-2 text-border">|</span>
                <span className="font-semibold text-foreground">{course.total_subtopics}</span> Topics
              </div>
            </div>
            <div className="hidden lg:block opacity-0 animate-fade-up animate-delay-200">
              <img
                src={HERO_IMG}
                alt="Salesforce Admin Training"
                className="rounded-2xl shadow-lg border border-border/50 w-full max-w-md mx-auto"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-muted/30" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            <div className="text-center">
              <p className="font-heading font-bold text-2xl sm:text-3xl text-sf-blue">{progress}%</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Overall Progress</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">{completedSubtopics.length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Topics Completed</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">{completedSections}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Sections Done</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">{Object.keys(completedQuizzes).length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Quizzes Passed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certificate CTA */}
      {progress === 100 && Object.keys(completedQuizzes).length >= 21 && (
        <section className="border-b border-border" data-testid="certificate-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100/50 dark:from-emerald-950 dark:to-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-6 flex flex-col sm:flex-row items-center gap-4 sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground">Course Complete!</h3>
                  <p className="text-sm text-muted-foreground">You may now return to the main LMS dashboard to view your official certificate.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Sections Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="sections-grid">
        <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-foreground mb-2">
          Course Modules
        </h2>
        <p className="text-muted-foreground mb-8">
          Work through each section and complete the quiz to track your progress.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {course.sections.map((section, idx) => {
            const stIds = section.subtopics.map(st => st.id);
            const sProgress = getSectionProgress(stIds);
            const quizDone = isQuizComplete(section.id);
            const isComplete = sProgress === 100 && quizDone;

            return (
              <Card
                key={section.id}
                className="group cursor-pointer border border-border hover:shadow-md hover:border-sf-blue/30 transition-all duration-200 overflow-hidden"
                onClick={() => navigate(`/section/${section.id}`)}
                data-testid={`section-card-${section.id}`}
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={section.image}
                    alt={section.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                    <span className="text-white/90 text-xs font-medium bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">
                      Section {section.id}
                    </span>
                    {isComplete && (
                      <span className="text-white text-xs font-medium bg-sf-success/80 backdrop-blur-sm px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Done
                      </span>
                    )}
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-heading font-semibold text-sm text-foreground mb-1 line-clamp-2 group-hover:text-sf-blue transition-colors">
                    {section.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    {section.subtopic_count} topics
                    {section.reading_time && (
                      <span className="ml-1.5">
                        <Clock className="h-3 w-3 inline-block mr-0.5 -mt-0.5" />
                        {section.reading_time} min
                      </span>
                    )}
                    {quizDone && <span className="ml-1.5 text-sf-success">+ Quiz passed</span>}
                  </p>
                  {section.takeaway && (
                    <p className="text-xs text-muted-foreground/80 mb-3 line-clamp-2 italic">
                      Takeaway: {section.takeaway}
                    </p>
                  )}
                  <Progress value={sProgress} className="h-1.5" />
                  <p className="text-xs text-muted-foreground mt-1.5">{sProgress}% complete</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Bookmarked Topics */}
      {bookmarks.length > 0 && course && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="bookmarks-section">
          <div className="flex items-center gap-2 mb-6">
            <Bookmark className="h-5 w-5 text-sf-warning fill-sf-warning" />
            <h2 className="font-heading font-semibold text-xl tracking-tight text-foreground">
              Bookmarked Topics
            </h2>
            <span className="text-xs bg-sf-warning/10 text-sf-warning font-medium px-2 py-0.5 rounded-full">
              {bookmarks.length}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {bookmarks.map(bmId => {
              const sec = course.sections.find(s => s.subtopics.some(st => st.id === bmId));
              const st = sec?.subtopics.find(s => s.id === bmId);
              if (!sec || !st) return null;
              return (
                <div
                  key={bmId}
                  className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg cursor-pointer hover:shadow-sm hover:border-sf-warning/30 transition-all duration-200"
                  onClick={() => navigate(`/section/${sec.id}`)}
                  data-testid={`bookmark-card-${bmId}`}
                >
                  <Bookmark className="h-4 w-4 text-sf-warning fill-sf-warning flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{st.title}</p>
                    <p className="text-xs text-muted-foreground">Section {sec.id}: {sec.title}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
